import { FeatureCollection, Point } from 'geojson'
import {
  loadServiceCalibPinImages,
  registerServiceCalibStyleImageMissingHandler,
  resolveStationMapIconIdFromLots,
  SERVICE_CALIB_MAP_ICON_SIZE_LAYOUT,
  ServiceCalibIconId,
} from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibMapIcons'
import { useCallback, useEffect, useMemo } from 'react'
import { Layer, Source } from 'react-map-gl'
import { ILot } from 'utils/interfaces'

export type StationMapPin = {
  point: Point
  lots?: ILot[]
  iconId?: ServiceCalibIconId
}

const layerStationStyle: any = {
  id: 'station-image-style',
  type: 'symbol',
  layout: {
    'icon-image': ['get', 'iconId'],
    'icon-anchor': 'center',
    'icon-rotation-alignment': 'map',
    'icon-size': SERVICE_CALIB_MAP_ICON_SIZE_LAYOUT,
    'icon-allow-overlap': true,
  },
  paint: {
    'icon-opacity': 1,
  },
}

export default function StationsOnMap({
  map,
  stations,
  pins,
  onPointChanged,
}: {
  map: any | undefined
  /** @deprecated Prefer `pins` — kept for simple point-only overlays. */
  stations?: Point[]
  pins?: StationMapPin[]
  onPointChanged?: (point: Point) => void
}) {
  const resolvedPins = useMemo<StationMapPin[]>(() => {
    if (pins?.length) {
      return pins
    }
    return (stations ?? []).map(point => ({ point }))
  }, [pins, stations])

  const stationPointData = useMemo<FeatureCollection<Point>>(() => {
    return {
      type: 'FeatureCollection',
      features: resolvedPins.map(pin => {
        const iconId = pin.iconId ?? resolveStationMapIconIdFromLots(pin.lots ?? [])
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: pin.point.coordinates,
          },
          properties: {
            iconId,
          },
        }
      }),
    }
  }, [resolvedPins])

  useEffect(() => {
    if (!map) {
      return
    }
    void loadServiceCalibPinImages(map)
    const unregister = registerServiceCalibStyleImageMissingHandler(map)
    const onStyleLoad = () => {
      void loadServiceCalibPinImages(map)
    }
    map.on('style.load', onStyleLoad)
    return () => {
      unregister()
      map.off('style.load', onStyleLoad)
    }
  }, [map])

  const onMapClicked = useCallback(
    (e: any) => {
      onPointChanged && onPointChanged({ type: 'Point', coordinates: [e.lngLat.lng, e.lngLat.lat] })
    },
    [onPointChanged]
  )

  useEffect(() => {
    map?.on('click', onMapClicked)
    return () => {
      map?.off('click', onMapClicked)
    }
  }, [map, onMapClicked])

  if (stationPointData.features.length === 0) {
    return null
  }

  return (
    <Source id="station" type="geojson" data={stationPointData}>
      <Layer {...layerStationStyle} />
    </Source>
  )
}
